import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { searchTags } from "./api";
import { transformSearchTagsResponse } from "./transformers";
import { filterTagRecommendQuery } from "@/utils/text-utils";

/**
 * Provides a search function for tags that can be called on demand (e.g., form submit).
 * - Returns a search method that takes a query string as input
 * - Cancels in-flight requests when a new search is triggered
 * - Transforms results into the standard Tag format
 */
export const useTagSearch = () => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: async (query: string) => {
      // Cancel any previous in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const filteredQuery = filterTagRecommendQuery(query);
      const data = await searchTags(filteredQuery, controller.signal);

      return transformSearchTagsResponse(data);
    },
  });

  const search = (query: string) => {
    if (!query || query.trim().length === 0) {
      return Promise.resolve([]);
    }
    return mutation.mutateAsync(query);
  };

  const clearSearch = () => {
    mutation.reset();
  };

  return {
    search,
    tags: mutation.data ?? [],
    error: mutation.error,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    clearSearch,
  };
};
